import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ChevronDownIcon } from '@heroicons/react/24/outline'
import { Button } from '@cottage/design-system/internal-tooling/ui'
import type { CompareColumns, CompareMode } from '@/store/projectionsStore'
import { formatMonthLabel } from './projectionsFormat'
import { useViewportAnchoredMenu } from './useViewportAnchoredMenu'

type GridCompareMenuProps = {
  compareMode: CompareMode
  onCompareModeChange: (mode: CompareMode) => void
  compareColumns: CompareColumns
  onToggleCompareColumn: (month: string) => void
  visibleSnapshotMonths: string[]
  visibleSnapshotCount: number
  onOpenAccuracyReport: () => void
}

function CompareModeSegmented({
  compareMode,
  onCompareModeChange,
}: {
  compareMode: CompareMode
  onCompareModeChange: (mode: CompareMode) => void
}) {
  return (
    <div className="flex items-center gap-2 px-3 py-2">
      <span className="text-sm text-gray-600">Compare Mode</span>
      <div
        className="inline-flex min-w-max gap-1 rounded-lg bg-gray-200 p-1"
        role="tablist"
        aria-label="Compare Mode"
      >
        {(
          [
            ['off', 'Off'],
            ['on', 'On'],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={compareMode === id}
            onClick={() => onCompareModeChange(id)}
            className={`relative whitespace-nowrap rounded-md px-2.5 py-1 text-xs font-medium transition-all ${
              compareMode === id
                ? 'bg-white text-brand-700 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  )
}

export default function GridCompareMenu({
  compareMode,
  onCompareModeChange,
  compareColumns,
  onToggleCompareColumn,
  visibleSnapshotMonths,
  visibleSnapshotCount,
  onOpenAccuracyReport,
}: GridCompareMenuProps) {
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const closeMenu = useCallback(() => setOpen(false), [])
  const { triggerRef, menuRef, menuStyle } = useViewportAnchoredMenu(open, closeMenu, {
    minWidthPx: 14 * 16,
  })

  useEffect(() => {
    if (!open) return
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node
      if (containerRef.current?.contains(target)) return
      if (menuRef.current?.contains(target)) return
      setOpen(false)
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [open, menuRef])

  const buttonLabel =
    compareMode === 'on'
      ? `Compare · On (${visibleSnapshotCount} mo)`
      : 'Compare · Off'

  const menuPanel =
    open && menuStyle ? (
      <div
        ref={menuRef}
        role="menu"
        data-viewport-menu
        className="fixed z-[200] overflow-hidden rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
        style={{
          top: menuStyle.top,
          left: menuStyle.left,
          minWidth: menuStyle.minWidth,
        }}
      >
        <CompareModeSegmented compareMode={compareMode} onCompareModeChange={onCompareModeChange} />
        <div className="my-1 border-t border-gray-100" role="separator" />
        <p className="px-3 pb-1 pt-1 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
          Pinned months
        </p>
        {visibleSnapshotMonths.length === 0 ? (
          <p className="px-3 pb-2 text-sm text-gray-500">No snapshots in the history window</p>
        ) : (
          visibleSnapshotMonths.map((month) => {
            const checked = compareColumns === 'all' || compareColumns.includes(month)
            return (
              <label
                key={month}
                className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => onToggleCompareColumn(month)}
                  className="rounded border-gray-300 text-brand-600 focus:ring-brand-600"
                />
                {formatMonthLabel(month)}
              </label>
            )
          })
        )}
        <div className="my-1 border-t border-gray-100" role="separator" />
        <button
          type="button"
          role="menuitem"
          className="flex w-full items-center px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
          onClick={() => {
            setOpen(false)
            onOpenAccuracyReport()
          }}
        >
          Accuracy report…
        </button>
      </div>
    ) : null

  return (
    <div className="relative" ref={containerRef}>
      <div ref={triggerRef} className="inline-flex">
        <Button
          variant="outline"
          size="sm"
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={() => setOpen((value) => !value)}
        >
          {buttonLabel}
          <ChevronDownIcon
            className={`ml-1 h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`}
            aria-hidden
          />
        </Button>
      </div>
      {menuPanel ? createPortal(menuPanel, document.body) : null}
    </div>
  )
}
